import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { FavoriteItem, Product } from '@/types';
import { useAuthStore } from '@/stores/auth';
import { useNotifications } from '@/composables/useNotifications';

export const useFavoritesStore = defineStore('favorites', () => {
  const items = ref<FavoriteItem[]>([]);
  const isLoading = ref(false);
  
  const { showNotification } = useNotifications();
  const authStore = useAuthStore();

  const favoriteCount = computed(() => items.value.length);
  
  const isFavorite = (productId: string) => {
    return items.value.some(item => item.productId === productId);
  };

  const addToFavorites = async (product: Product) => {
    try {
      if (isFavorite(product.id)) {
        showNotification('El producto ya está en favoritos', 'info');
        return;
      }

      const favoriteItem: FavoriteItem = {
        id: Date.now().toString(),
        productId: product.id,
        product,
        addedAt: new Date(),
      };

      items.value.push(favoriteItem);
      await saveFavorites();
      showNotification(`${product.name} añadido a favoritos`, 'success');
    } catch (error) {
      showNotification('Error al añadir a favoritos', 'error');
    }
  };

  const removeFromFavorites = async (productId: string) => {
    try {
      const itemIndex = items.value.findIndex(item => item.productId === productId);
      if (itemIndex >= 0) {
        const itemName = items.value[itemIndex].product.name;
        items.value.splice(itemIndex, 1);
        await saveFavorites();
        showNotification(`${itemName} eliminado de favoritos`, 'success');
      }
    } catch (error) {
      showNotification('Error al eliminar de favoritos', 'error');
    }
  };

  const toggleFavorite = async (product: Product) => {
    if (isFavorite(product.id)) {
      await removeFromFavorites(product.id);
    } else {
      await addToFavorites(product);
    }
  };

  const clearFavorites = async () => {
    try {
      items.value = [];
      await saveFavorites();
      showNotification('Favoritos vaciados', 'success');
    } catch (error) {
      showNotification('Error al vaciar favoritos', 'error');
    }
  };

  const saveFavorites = async () => {
    const favoritesData = {
      items: items.value,
      userId: authStore.user?.id,
    };
    
    if (authStore.isAuthenticated) {
      // Save to server (simulated)
      localStorage.setItem(`favorites_${authStore.user?.id}`, JSON.stringify(favoritesData));
    } else {
      // Save locally
      localStorage.setItem('favorites_guest', JSON.stringify(favoritesData));
    }
  };

  const loadFavorites = async () => {
    try {
      let favoritesData;
      
      if (authStore.isAuthenticated) {
        // Load from server (simulated)
        const serverData = localStorage.getItem(`favorites_${authStore.user?.id}`);
        const guestData = localStorage.getItem('favorites_guest');
        
        if (serverData) {
          favoritesData = JSON.parse(serverData);
        } else if (guestData) {
          // Migrate guest favorites to user account
          favoritesData = JSON.parse(guestData);
          localStorage.removeItem('favorites_guest');
          await saveFavorites();
        }
      } else {
        // Load guest favorites
        const guestData = localStorage.getItem('favorites_guest');
        if (guestData) {
          favoritesData = JSON.parse(guestData);
        }
      }

      if (favoritesData?.items) {
        items.value = favoritesData.items.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt),
        }));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  return {
    items: computed(() => items.value),
    isLoading: computed(() => isLoading.value),
    favoriteCount,
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    clearFavorites,
    loadFavorites,
  };
});